// Single source of truth for the main menu — consumed by BOTH the desktop
// taskbar tray (all groups) and the mobile drawer (all but System, which the
// drawer renders as its own bottom "System & tools" section).
//
// Reorganized 2026-07-30 around the folder-navigator workflow: "Browse" leads
// with every data entity that expands as a recursive tree (Clients › client ›
// Jobs › job › … › files); working views and tools follow.
export interface AppMenuItem {
  href: string;
  icon: string;
  label: string;
  /** External URL — opens a new browser tab instead of routing. */
  external?: boolean;
}

export interface AppMenuGroup {
  label: string;
  icon: string;
  items: AppMenuItem[];
}

export const APP_MENU_GROUPS: AppMenuGroup[] = [
  {
    label: 'Browse',
    icon: 'folder_open',
    items: [
      { href: '/clients', icon: 'groups', label: 'Clients' },
      { href: '/crews', icon: 'diversity_3', label: 'Crews' },
      { href: '/vendors', icon: 'storefront', label: 'Vendors' },
      { href: '/purchase-orders', icon: 'local_shipping', label: 'Purchase Orders' },
    ],
  },
  {
    label: 'Workspace',
    icon: 'space_dashboard',
    items: [
      { href: '/calendar', icon: 'calendar_month', label: 'Calendar' },
      { href: '/planner', icon: 'view_kanban', label: 'Planner' },
      { href: '/scheduling', icon: 'grid_view', label: 'Scheduling' },
      { href: '/drafting', icon: 'architecture', label: 'Drafting' },
    ],
  },
  {
    label: 'Finance',
    icon: 'payments',
    items: [
      // #151: reachable here, but not top-level — the day-to-day path to these
      // is through the client or job they belong to.
      // #199: Agreements removed from the menu — they now live on the job
      // detail page (clients › jobs › agreements). /agreements still routes
      // for old links and windows.
      { href: '/payroll', icon: 'payments', label: 'Payroll' },
      { href: '/reports', icon: 'bar_chart', label: 'Reports' },
      { href: '/projections', icon: 'query_stats', label: 'Projections' },
      { href: '/market-rates', icon: 'price_check', label: 'Market Rates' },
      { href: '/equipment', icon: 'handyman', label: 'Equipment' },
      { href: '/company-docs', icon: 'verified_user', label: 'Company Docs' },
      { href: '/site-analytics', icon: 'monitoring', label: 'Site Analytics' },
    ],
  },
  {
    label: 'Tools',
    icon: 'construction',
    items: [
      { href: '/outreach', icon: 'campaign', label: 'Outreach' },
      { href: '/blog-docket', icon: 'rss_feed', label: 'Blog' },
      { href: '/mail', icon: 'mail', label: 'Mail' },
      { href: '/catalog', icon: 'inventory_2', label: 'Catalog' },
      { href: '/explorer', icon: 'folder_open', label: 'File Explorer' },
      { href: '/bc-code', icon: 'menu_book', label: 'BC Building Code' },
      { href: '/calculator', icon: 'calculate', label: 'Calculator' },
    ],
  },
  {
    label: 'System',
    icon: 'settings',
    items: [
      { href: '/settings', icon: 'settings', label: 'Settings' },
      { href: '/my-tasks', icon: 'checklist', label: 'My Tasks' },
      { href: '/roadmap', icon: 'map', label: 'Roadmap' },
      { href: '/help', icon: 'help', label: 'Help Center' },
      { href: 'https://admin.ochrepoint.ca', icon: 'monitoring', label: 'Admin', external: true },
    ],
  },
];
